import React, { useCallback, useState } from 'react';
import {
  Box,
  ButtonGroup,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Popover,
  Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import NearMeOutlinedIcon from '@mui/icons-material/NearMeOutlined';
import TextFieldsOutlinedIcon from '@mui/icons-material/TextFieldsOutlined';
import CropSquareOutlinedIcon from '@mui/icons-material/CropSquareOutlined';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import HorizontalRuleOutlinedIcon from '@mui/icons-material/HorizontalRuleOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ImageSearchOutlinedIcon from '@mui/icons-material/ImageSearchOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import RedoOutlinedIcon from '@mui/icons-material/RedoOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import FlipToFrontOutlinedIcon from '@mui/icons-material/FlipToFrontOutlined';
import FlipToBackOutlinedIcon from '@mui/icons-material/FlipToBackOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import ZoomInOutlinedIcon from '@mui/icons-material/ZoomInOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';

import { setActiveTool, setActiveShapeType, setZoom } from '../slideEditorSlice';
import type { SlideEditorState } from '../slideEditorSlice';
import { getAllWidgets } from '../interactives/widgetRegistry';

import SlideLayoutPicker from './SlideLayoutPicker';
import SlideThemePicker from './SlideThemePicker';
import ToolbarTextStyle from './ToolbarTextStyle';
import ToolbarShapeStyle from './ToolbarShapeStyle';
import ToolbarSlideBackground from './ToolbarSlideBackground';

import type { ThemeId } from '@/shared/theming/registry';
import type { SlideTemplate, SlideTextStyle, InteractiveWidgetType } from '@/shared/types/slide';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/redux';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Use ⌘ on macOS, Ctrl elsewhere */
const MOD =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘' : 'Ctrl';

const ZOOM_PRESETS = [50, 75, 90, 100, 125, 150, 200];

// ---------------------------------------------------------------------------
// SlideToolbar
// ---------------------------------------------------------------------------

interface SlideToolbarProps {
  slideElements: {
    selectedElement: import('@/shared/types/slide').SlideElement | null;
    selectedElementResolved: import('@/shared/theming/resolve').ResolvedSlideElement | null;
    selectedElementIds: string[];
    removeElement: (id: string) => void;
    removeElements: (ids: string[]) => void;
    duplicateElement: (id: string) => string | undefined;
    updateElement: (
      id: string,
      updates: Partial<import('@/shared/types/slide').SlideElement>
    ) => void;
    moveToFront: (id: string) => void;
    moveToBack: (id: string) => void;
  };
  canEdit?: boolean;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onAddSlide?: (template: SlideTemplate) => void;
  /** Apply a deck-wide theme (§7). */
  onApplyTheme?: (themeId: ThemeId) => void;
  /** The deck's active theme id — used to mark the active theme in the picker. */
  activeThemeId?: string;
  /** Insert an interactive widget element */
  onInsertInteractive?: (widgetType: InteractiveWidgetType) => void;
  /** Open the image library dialog */
  onInsertImageFromLibrary?: () => void;
  /** Open the video library dialog on the requested tab */
  onInsertVideo?: (mode: 'search' | 'url') => void;
  /** Number of unresolved image placeholders across all slides */
  pendingImageCount?: number;
  /** Navigate to & resolve the next unresolved image placeholder */
  onReviewPendingImages?: () => void;
  /** Current slide background color */
  slideBackgroundColor?: string;
  /** Callback to change the current slide's background color */
  onUpdateSlideBackground?: (color: string) => void;
  /** Launch presentation mode */
  onPresent?: () => void;
  /** Export the deck to Google Slides */
  onGoogleExport?: () => Promise<void> | void;
  googleExporting?: boolean;
}

const SlideToolbar: React.FC<SlideToolbarProps> = ({
  slideElements,
  canEdit = true,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onAddSlide,
  onApplyTheme,
  activeThemeId,
  onInsertInteractive,
  onInsertImageFromLibrary,
  onInsertVideo,
  pendingImageCount = 0,
  onReviewPendingImages,
  slideBackgroundColor,
  onUpdateSlideBackground,
  onPresent,
  onGoogleExport,
  googleExporting = false,
}) => {
  const dispatch = useAppDispatch();
  const activeToolType = useAppSelector((s) => s.slideEditor.activeToolType);
  const activeShapeType = useAppSelector((s) => s.slideEditor.activeShapeType);
  const zoom = useAppSelector((s) => s.slideEditor.zoom);

  const {
    selectedElement,
    selectedElementResolved,
    selectedElementIds,
    removeElements,
    duplicateElement,
    updateElement,
    moveToFront,
    moveToBack,
  } = slideElements;

  // Shape sub-menu popover
  const [shapeAnchor, setShapeAnchor] = useState<HTMLElement | null>(null);

  // Layout picker popover
  const [layoutAnchor, setLayoutAnchor] = useState<HTMLElement | null>(null);

  // Theme picker popover
  const [themeAnchor, setThemeAnchor] = useState<HTMLElement | null>(null);

  // Insert image menu
  const [imageAnchor, setImageAnchor] = useState<HTMLElement | null>(null);

  // Insert interactive menu
  const [interactiveAnchor, setInteractiveAnchor] = useState<HTMLElement | null>(null);

  // Kebab overflow menu
  const [kebabAnchor, setKebabAnchor] = useState<HTMLElement | null>(null);

  const handleToolChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, value: SlideEditorState['activeToolType'] | null) => {
      if (value) dispatch(setActiveTool(value));
    },
    [dispatch]
  );

  const handleShapeSelect = useCallback(
    (shape: SlideEditorState['activeShapeType']) => {
      dispatch(setActiveShapeType(shape));
      dispatch(setActiveTool('shape'));
      setShapeAnchor(null);
    },
    [dispatch]
  );

  const handleDelete = useCallback(() => {
    removeElements(selectedElementIds);
    setKebabAnchor(null);
  }, [selectedElementIds, removeElements]);

  const handleDuplicate = useCallback(() => {
    if (selectedElementIds.length === 1) duplicateElement(selectedElementIds[0]);
    setKebabAnchor(null);
  }, [selectedElementIds, duplicateElement]);

  const handleMoveToFront = useCallback(() => {
    if (selectedElement) moveToFront(selectedElement.id);
    setKebabAnchor(null);
  }, [selectedElement, moveToFront]);

  const handleMoveToBack = useCallback(() => {
    if (selectedElement) moveToBack(selectedElement.id);
    setKebabAnchor(null);
  }, [selectedElement, moveToBack]);

  const handleZoomChange = useCallback(
    (e: { target: { value: string | number } }) => {
      dispatch(setZoom(Number(e.target.value)));
    },
    [dispatch]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.5,
        height: 40,
        borderBottom: 1,
        borderColor: 'divider',
        flexWrap: 'nowrap',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {/* ---- New Slide (plus button with dropdown) ---- */}
      {canEdit && onAddSlide && (
        <>
          <ButtonGroup size="small" variant="outlined" sx={{ height: 30 }}>
            <Tooltip title="New slide">
              <Button onClick={() => onAddSlide('BLANK')} sx={{ minWidth: 0, px: 0.75 }}>
                <AddOutlinedIcon sx={{ fontSize: 18 }} />
              </Button>
            </Tooltip>
            <Tooltip title="Choose template">
              <Button onClick={(e) => setLayoutAnchor(e.currentTarget)} sx={{ minWidth: 0, px: 0 }}>
                <ArrowDropDownIcon sx={{ fontSize: 18 }} />
              </Button>
            </Tooltip>
          </ButtonGroup>
          <SlideLayoutPicker
            anchorEl={layoutAnchor}
            open={Boolean(layoutAnchor)}
            onClose={() => setLayoutAnchor(null)}
            onSelect={(template: SlideTemplate) => {
              onAddSlide(template);
              setLayoutAnchor(null);
            }}
          />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        </>
      )}

      {/* ---- Theme picker ---- */}
      {canEdit && onApplyTheme && activeThemeId && (
        <>
          <Tooltip title="Theme">
            <Button
              size="small"
              variant="outlined"
              startIcon={<PaletteOutlinedIcon sx={{ fontSize: 18 }} />}
              endIcon={<ArrowDropDownIcon sx={{ fontSize: 18 }} />}
              onClick={(e) => setThemeAnchor(e.currentTarget)}
              sx={{ height: 30, textTransform: 'none' }}
            >
              Theme
            </Button>
          </Tooltip>
          <SlideThemePicker
            anchorEl={themeAnchor}
            open={Boolean(themeAnchor)}
            activeThemeId={activeThemeId}
            onClose={() => setThemeAnchor(null)}
            onSelect={(themeId) => {
              onApplyTheme(themeId);
              setThemeAnchor(null);
            }}
          />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        </>
      )}

      {/* ---- Undo / Redo ---- */}
      {canEdit && (
        <>
          <Tooltip title={`Undo (${MOD}+Z)`}>
            <span>
              <IconButton size="small" disabled={!canUndo} onClick={onUndo}>
                <UndoOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={`Redo (${MOD}+Shift+Z)`}>
            <span>
              <IconButton size="small" disabled={!canRedo} onClick={onRedo}>
                <RedoOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Tooltip title="Zoom">
            <ZoomInOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          </Tooltip>

          <Select
            size="small"
            value={zoom}
            onChange={handleZoomChange}
            variant="standard"
            disableUnderline
            sx={{
              fontSize: 12,
              color: 'text.secondary',
              minWidth: 56,
              '& .MuiSelect-select': { py: 0, textAlign: 'center' },
            }}
            renderValue={(v) => `${v}%`}
          >
            {ZOOM_PRESETS.map((p) => (
              <MenuItem key={p} value={p} sx={{ fontSize: 13 }}>
                {p}%
              </MenuItem>
            ))}
          </Select>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        </>
      )}

      {/* ---- Tool select ---- */}
      {canEdit && (
        <>
          <ToggleButtonGroup
            value={activeToolType}
            exclusive
            onChange={handleToolChange}
            size="small"
          >
            <ToggleButton value="select" aria-label="Select">
              <Tooltip title="Select (V)">
                <span style={{ display: 'flex' }}>
                  <NearMeOutlinedIcon fontSize="small" />
                </span>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="text" aria-label="Text box">
              <Tooltip title="Text box (T)">
                <span style={{ display: 'flex' }}>
                  <TextFieldsOutlinedIcon fontSize="small" />
                </span>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Insert image — dropdown with library + URL options */}
          <Tooltip title="Insert image">
            <ToggleButton
              value="image"
              selected={activeToolType === 'image' || Boolean(imageAnchor)}
              size="small"
              onClick={(e) => setImageAnchor(e.currentTarget)}
              aria-label="Insert image"
            >
              <ImageOutlinedIcon fontSize="small" />
              <ArrowDropDownIcon sx={{ fontSize: 14, ml: -0.25 }} />
            </ToggleButton>
          </Tooltip>
          <Menu
            anchorEl={imageAnchor}
            open={Boolean(imageAnchor)}
            onClose={() => setImageAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            {onInsertImageFromLibrary && (
              <MenuItem
                onClick={() => {
                  onInsertImageFromLibrary();
                  setImageAnchor(null);
                }}
              >
                <ListItemIcon>
                  <CollectionsOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Search Library</ListItemText>
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                dispatch(setActiveTool('image'));
                setImageAnchor(null);
              }}
            >
              <ListItemIcon>
                <LinkOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>From URL (click canvas)</ListItemText>
            </MenuItem>
          </Menu>

          {/* Insert video — opens dialog directly on the Search tab.
              Teachers can switch to the From URL tab inside the dialog. */}
          {onInsertVideo && (
            <Tooltip title="Insert video">
              <ToggleButton
                value="video"
                selected={false}
                size="small"
                onClick={() => onInsertVideo('search')}
                aria-label="Insert video"
              >
                <VideocamOutlinedIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
          )}

          {/* Pending image placeholders badge */}
          {pendingImageCount > 0 && onReviewPendingImages && (
            <Tooltip
              title={
                pendingImageCount === 1
                  ? 'Review 1 pending image'
                  : `Review ${pendingImageCount} pending images`
              }
            >
              <Button
                size="small"
                variant="outlined"
                color="info"
                startIcon={<ImageSearchOutlinedIcon fontSize="small" />}
                onClick={onReviewPendingImages}
                sx={{ minHeight: 30, textTransform: 'none', ml: 0.5 }}
              >
                {pendingImageCount}
              </Button>
            </Tooltip>
          )}

          {/* Shape button with dropdown — visually grouped near tool select */}
          <Tooltip title="Shapes">
            <ToggleButton
              value="shape"
              selected={activeToolType === 'shape'}
              size="small"
              onClick={(e) => setShapeAnchor(e.currentTarget)}
              sx={{ ml: '-1px' }}
              aria-label="Shapes"
            >
              <CropSquareOutlinedIcon fontSize="small" />
              <ArrowDropDownIcon sx={{ fontSize: 14, ml: -0.25 }} />
            </ToggleButton>
          </Tooltip>
          <Popover
            open={Boolean(shapeAnchor)}
            anchorEl={shapeAnchor}
            onClose={() => setShapeAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Box sx={{ display: 'flex', p: 1, gap: 0.5 }}>
              <Tooltip title="Rectangle">
                <IconButton
                  size="small"
                  color={activeShapeType === 'rectangle' ? 'primary' : 'default'}
                  onClick={() => handleShapeSelect('rectangle')}
                >
                  <CropSquareOutlinedIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Circle">
                <IconButton
                  size="small"
                  color={activeShapeType === 'circle' ? 'primary' : 'default'}
                  onClick={() => handleShapeSelect('circle')}
                >
                  <CircleOutlinedIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Line">
                <IconButton
                  size="small"
                  color={activeShapeType === 'line' ? 'primary' : 'default'}
                  onClick={() => handleShapeSelect('line')}
                >
                  <HorizontalRuleOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Popover>

          {/* Insert interactive widget — dropdown menu */}
          {onInsertInteractive && (
            <>
              <Tooltip title="Interactive elements">
                <ToggleButton
                  value="interactive"
                  selected={false}
                  size="small"
                  onClick={(e) => setInteractiveAnchor(e.currentTarget)}
                  sx={{ ml: '-1px' }}
                  aria-label="Interactive elements"
                >
                  <WidgetsOutlinedIcon fontSize="small" />
                  <ArrowDropDownIcon sx={{ fontSize: 14, ml: -0.25 }} />
                </ToggleButton>
              </Tooltip>
              <Menu
                anchorEl={interactiveAnchor}
                open={Boolean(interactiveAnchor)}
                onClose={() => setInteractiveAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                {getAllWidgets().map((w) => {
                  const Icon = w.icon;
                  return (
                    <MenuItem
                      key={w.type}
                      onClick={() => {
                        onInsertInteractive(w.type);
                        setInteractiveAnchor(null);
                      }}
                    >
                      <ListItemIcon>
                        <Icon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>{w.name}</ListItemText>
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          )}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        </>
      )}

      {/* ---- Contextual text style controls ---- */}
      {/* Display the RESOLVED element; write the stored sparse style as base (§10.11). */}
      {canEdit && selectedElement?.type === 'text' && selectedElementResolved?.type === 'text' && (
        <>
          <ToolbarTextStyle
            element={selectedElementResolved}
            onUpdateStyle={(patch: Partial<SlideTextStyle>) =>
              updateElement(selectedElement.id, {
                style: { ...(selectedElement.style ?? {}), ...patch },
              })
            }
          />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        </>
      )}

      {/* ---- Contextual shape style controls ---- */}
      {canEdit &&
        selectedElement?.type === 'shape' &&
        selectedElementResolved?.type === 'shape' && (
          <>
            <ToolbarShapeStyle
              element={selectedElementResolved}
              onUpdateContent={(patch) =>
                updateElement(selectedElement.id, {
                  content: { ...selectedElement.content, ...patch },
                } as never)
              }
            />
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          </>
        )}

      {/* ---- Slide background ---- */}
      {canEdit && onUpdateSlideBackground && (
        <ToolbarSlideBackground
          color={slideBackgroundColor ?? '#ffffff'}
          onChange={onUpdateSlideBackground}
        />
      )}

      {/* ---- Spacer ---- */}
      <Box sx={{ flex: 1 }} />

      {/* ---- Present button ---- */}
      {onGoogleExport && (
        <Tooltip
          title={googleExporting ? 'Exporting to Google Slides...' : 'Export to Google Slides'}
        >
          <span>
            <Button
              size="small"
              sx={{ height: 32 }}
              variant="outlined"
              startIcon={
                googleExporting ? (
                  <CircularProgress size={16} thickness={5} />
                ) : (
                  <CloudUploadOutlinedIcon />
                )
              }
              onClick={() => void onGoogleExport()}
              disabled={googleExporting}
            >
              Google Slides
            </Button>
          </span>
        </Tooltip>
      )}
      {onPresent && (
        <Tooltip title="Present">
          <Button
            size="small"
            sx={{ height: 32 }}
            variant="outlined"
            startIcon={<SlideshowOutlinedIcon />}
            onClick={onPresent}
          >
            Present
          </Button>
        </Tooltip>
      )}

      {/* ---- Kebab overflow menu ---- */}
      {canEdit && (
        <>
          <Tooltip title="More actions">
            <IconButton size="small" onClick={(e) => setKebabAnchor(e.currentTarget)}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={kebabAnchor}
            open={Boolean(kebabAnchor)}
            onClose={() => setKebabAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem disabled={selectedElementIds.length !== 1} onClick={handleDuplicate}>
              <ListItemIcon>
                <ContentCopyOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Duplicate</ListItemText>
              <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                {MOD}+D
              </Typography>
            </MenuItem>
            <MenuItem disabled={!selectedElement} onClick={handleMoveToFront}>
              <ListItemIcon>
                <FlipToFrontOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Bring to front</ListItemText>
            </MenuItem>
            <MenuItem disabled={!selectedElement} onClick={handleMoveToBack}>
              <ListItemIcon>
                <FlipToBackOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Send to back</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem
              disabled={selectedElementIds.length === 0}
              onClick={handleDelete}
              sx={{
                '&:not(.Mui-disabled):hover': {
                  color: 'error.main',
                  '& .MuiListItemIcon-root': { color: 'error.main' },
                },
              }}
            >
              <ListItemIcon>
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
              <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                ⌫
              </Typography>
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );
};

export default SlideToolbar;
